import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "antd";
import { isArray } from "../../lib/utility";

const EllipsisTooltip = ({ title, enabled, children }) => {
  const [truncated, setTruncated] = useState(false);

  if (!enabled) return children;

  const checkTruncated = (e) => {
    const el = e.currentTarget;
    setTruncated(el.scrollWidth > el.clientWidth);
    children.props.onMouseEnter?.(e);
  };

  return (
    <Tooltip
      title={truncated ? title : ""}
      placement="top"
      mouseEnterDelay={0.2}
      getPopupContainer={() => document.body}
      zIndex={1000000}
      styles={{ root: { maxWidth: 360, wordBreak: "break-word" } }}
    >
      {React.cloneElement(children, { onMouseEnter: checkTruncated })}
    </Tooltip>
  );
};

function getOptionLabelText(label) {
  if (label == null || typeof label === "boolean") return "";
  if (typeof label === "string" || typeof label === "number") {
    return String(label);
  }
  if (Array.isArray(label)) {
    return label
      .map((part) => getOptionLabelText(part))
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }
  return "";
}

const Select = ({
  options,
  onChange,
  value,
  labelValue,
  placeholder,
  disabled,
  log = false,
  forceBelow = false,
  style = {},
  className = "",
  onFocus,
  searchable = true,
  /** Fixed narrow menus (e.g. time pickers). Also used as min when max/width are set. */
  dropdownMinWidth,
  /** Cap how wide the open menu can grow. Pass with dropdownMinWidth to size freely. */
  dropdownMaxWidth,
  /** Exact open-menu width in px (overrides min/max grow). */
  dropdownWidth,
  menuClassName = "",
  ellipsisTooltip = false,
}) => {
  const { t } = useTranslation("common");
  const defaultSelectPlaceholder = t("commonUi.select", {
    defaultValue: "-Select-",
  });
  // Treat missing / English "-Select-" as the i18n default so HI/RU/AR show correctly.
  const resolvedPlaceholder =
    placeholder === undefined ||
    placeholder === "" ||
    placeholder === "-Select-"
      ? defaultSelectPlaceholder
      : placeholder;
  const [selectedValue, setSelectedValue] = useState(labelValue ?? value ?? "");
  const [selectedId, setSelectedId] = useState(value ?? "");
  const [focused, setFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);

  const selectRef = useRef(null);
  const dropdownRef = useRef(null);
  const selectDomId = React.useId().replace(/:/g, "");

  const [menuPos, setMenuPos] = useState({ x: 0, y: 0, width: 0, height: 0 });

  useEffect(() => {
    if (!focused || !selectRef.current) return;
    const updatePosition = () => {
      const rect = selectRef.current.getBoundingClientRect();
      setMenuPos({
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [focused]);

  const { y, x, width, height } = menuPos;
  // Old callers that only set dropdownMinWidth (time pickers) keep a fixed compact menu.
  // Pass dropdownMaxWidth and/or dropdownWidth for tunable open-menu size.
  const compactMenu =
    dropdownMinWidth != null &&
    dropdownMaxWidth == null &&
    dropdownWidth == null;
  const menuWidth = Math.max(
    width,
    Number(dropdownWidth ?? dropdownMinWidth) || 0
  );
  const triggerWidth =
    width > 0
      ? width
      : selectRef.current
        ? selectRef.current.getBoundingClientRect().width
        : 0;
  const effectiveMenuWidth =
    dropdownWidth != null
      ? Number(dropdownWidth)
      : compactMenu
        ? menuWidth
        : (triggerWidth || 0);

  const safeLeft =
    effectiveMenuWidth > 0 && typeof window !== "undefined"
      ? Math.max(8, Math.min(x, window.innerWidth - effectiveMenuWidth - 8))
      : x;

  const above =
    !forceBelow && window.innerHeight - y - height - 3 < 200;

  const sanitizedOptions = isArray(options)
    ? options.filter((opt) => opt && opt.id !== undefined)
    : [];

  const handleSelectClick = (e) => {
    if (e.target.closest(`[data-select-trigger="${selectDomId}"]`)) {
      setFocused((prev) => {
        if (!prev) onFocus?.();
        return !prev;
      });
    }
  };

  const handleOutSidePointer = useCallback((e) => {
    if (
      selectRef.current?.contains(e.target) ||
      dropdownRef.current?.contains(e.target)
    ) {
      return;
    }
    setFocused(false);
  }, []);

  const handleOptionSelect = (opt) => {
    const nextId = String(opt.id);
    // Re-selecting the active option should keep the value (native <select> behavior),
    // not clear it — clearing caused empty payloads (e.g. dashboard days: "").
    if (
      nextId === String(selectedId) ||
      (nextId === "%" && String(selectedId).toLowerCase() === "all") ||
      (nextId.toLowerCase() === "all" && String(selectedId) === "%")
    ) {
      setSelectedValue(getOptionLabelText(opt.label) || nextId);
      setSearchQuery("");
      setFocused(false);
      return;
    }
    setSelectedId(nextId);
    setSelectedValue(getOptionLabelText(opt.label) || nextId);
    onChange(nextId);
    setSearchQuery("");
    setFocused(false);
  };

  useEffect(() => {
    if (!focused) return;
    document.addEventListener("mousedown", handleOutSidePointer);
    return () => {
      document.removeEventListener("mousedown", handleOutSidePointer);
    };
  }, [focused, handleOutSidePointer]);

  useEffect(() => {
    if (focused && searchable && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [focused, searchable]);

  // Sync from parent when controlled. Skip when uncontrolled (`value` and `labelValue`
  // omitted): `sanitizedOptions` is a new array every render and would wipe local selection.
  useEffect(() => {
    if (value === undefined && labelValue === undefined) {
      return;
    }
    const nextId = value ?? "";
    const matchedOption = sanitizedOptions.find(
      (opt) =>
        String(opt.id) === String(nextId) ||
        (String(nextId).toLowerCase() === "all" && String(opt.id) === "%") ||
        (String(nextId) === "%" && String(opt.id).toLowerCase() === "all")
    );

    setSelectedId(nextId);
    const resolvedLabel =
      labelValue != null && labelValue !== ""
        ? labelValue
        : getOptionLabelText(matchedOption?.label);
    setSelectedValue(resolvedLabel || (nextId ? String(nextId) : ""));
  }, [value, labelValue, sanitizedOptions]);

  const filteredOptions = isArray(sanitizedOptions)
    ? searchable
      ? sanitizedOptions.filter((opt) =>
          getOptionLabelText(opt.label)
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        )
      : sanitizedOptions
    : [];

  return (
    <div
      className={
        "customSelect fw-400 d-flex align-items-center " +
        (focused ? "focus " : "") +
        className
      }
      onClick={handleSelectClick}
      ref={selectRef}
      data-select-trigger={selectDomId}
      style={{
        ...style,
        ...(disabled
          ? { pointerEvents: "none", backgroundColor: "#00000014" }
          : {}),
      }}
    >
      <style>
        {`
          .customSelect:not(.on-brand-header) {
            position: relative;
            border: 1px solid #ced4da;
            padding: 6px 28px 6px 12px;
            border-radius: 4px;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
            background-repeat: no-repeat;
            background-position: right .75rem center;
            background-size: 16px 12px;
            background-color: white;
            cursor: pointer;
            user-select: none;
          }
          .customSelect.on-brand-header {
            position: relative;
            border: 1px solid rgba(255, 255, 255, 0.92);
            padding: 6px 28px 6px 12px;
            border-radius: 8px;
            background-color: transparent;
            background-repeat: no-repeat;
            background-position: right 0.75rem center;
            background-size: 16px 12px;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
            cursor: pointer;
            user-select: none;
          }
          .customSelect.on-brand-header > p,
          .customSelect.on-brand-header > p.text-gray,
          .customSelect.on-brand-header > p.text-black {
            color: #fff !important;
          }
          .customSelect.on-brand-header.focus {
            border-color: #fff;
            box-shadow: 0 0 0 2px rgb(255 255 255 / 0.28);
          }
          .customSelect.focus {
            border-color: #CE20B0;
            outline: 0;
            box-shadow: 0 0 0 0.25rem rgba(206, 32, 176, 0.25);
          }
          .customSelectDropdown .option:hover {
            background: #CE20B0;
            color: white;
          }
          .customSelectDropdown .option:hover p {
            color: white !important;
          }
          .customSelect > p {
            font-weight: 400;
            font-size: 15px;
            cursor: pointer;
            user-select: none;
          }
          .customSelect > p.text-gray {
            color: gray;
          }
          .customSelect > p {
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .customSelect > p::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          .customSelectDropdown {
            position: fixed;
            padding: 6px;
            display: block;
            z-index: 999999;
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            overflow-y: auto;
            overflow-x: hidden;
            max-height: 220px;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
            box-sizing: border-box;
          }
          .customSelectDropdown::-webkit-scrollbar {
            width: 5px;
          }
          .customSelectDropdown::-webkit-scrollbar-track {
            background: transparent;
          }
          .customSelectDropdown::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 4px;
          }
          .customSelectDropdown::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          .customSelectDropdown .search-container {
            position: sticky;
            top: -6px;
            background: #ffffff;
            z-index: 5;
            margin: -6px -6px 6px -6px;
            padding: 6px;
            border-bottom: 1px solid #f1f5f9;
          }
          .customSelectDropdown .search-container .form-control {
            font-size: 13.5px;
            height: 34px;
            padding: 4px 10px;
            border-radius: 6px;
            border: 1px solid #d1d5db;
          }
          .customSelectDropdown .search-container .form-control:focus {
            border-color: #CE20B0;
            outline: 0;
            box-shadow: 0 0 0 2px rgba(206, 32, 176, 0.2);
          }
          .customSelectDropdown .option {
            cursor: pointer;
            border-radius: 6px;
            padding: 6px 10px;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
            margin-bottom: 2px;
            transition: background-color 0.15s ease, color 0.15s ease;
          }
          .customSelectDropdown .option:hover {
            background: #CE20B0;
            color: white;
          }
          .customSelectDropdown .option:hover p {
            color: white !important;
          }
          .customSelectDropdown .option.active {
            background: #CE20B0;
            color: white;
          }
          .customSelectDropdown .option.active p {
            color: white !important;
            font-weight: 500;
          }
          .customSelectDropdown .option input {
            height: 16px;
            width: 16px;
            border-radius: 8px;
          }
          .customSelectDropdown .option p {
            font-size: 13.5px;
            font-weight: 400;
            line-height: 1.4;
            word-break: break-word;
            overflow-wrap: anywhere;
            white-space: normal;
            min-width: 0;
            margin: 0;
          }
          .customSelectDropdown.compact-time-menu {
            min-width: 72px;
          }
          .customSelectDropdown.compact-time-menu .option p {
            white-space: nowrap;
            word-break: keep-all;
            overflow: visible;
            font-size: 12px;
          }
        `}
      </style>
      <EllipsisTooltip
        enabled={ellipsisTooltip}
        title={focused ? "" : selectedValue}
      >
        <p
          tabIndex={-1}
          className={
            "m-0 " +
            (!selectedId || selectedId.length === 0 ? "text-gray" : "text-black")
          }
          style={{
            textOverflow:
              className.includes("on-brand-header") || ellipsisTooltip
                ? "ellipsis"
                : "clip",
            whiteSpace:
              className.includes("on-brand-header") ||
              compactMenu ||
              ellipsisTooltip
                ? "nowrap"
                : "normal",
            lineHeight: 1.35,
            wordBreak:
              className.includes("on-brand-header") ||
              compactMenu ||
              ellipsisTooltip
                ? "normal"
                : "break-word",
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          {!selectedValue ? resolvedPlaceholder : selectedValue}
        </p>
      </EllipsisTooltip>
      {focused &&
        createPortal(
          <div
            ref={dropdownRef}
            className={
              "customSelectDropdown " +
              (above ? "above" : "below") +
              (compactMenu ? " compact-time-menu" : "") +
              (menuClassName ? ` ${menuClassName}` : "")
            }
            tabIndex={-1}
            style={{
              left: `${safeLeft}px`,
              ...(dropdownWidth != null
                ? {
                    width: `${Number(dropdownWidth)}px`,
                    minWidth: `${Number(dropdownWidth)}px`,
                    maxWidth: `${Number(dropdownWidth)}px`,
                  }
                : compactMenu
                  ? {
                      width: `${menuWidth}px`,
                      minWidth: dropdownMinWidth
                        ? `${dropdownMinWidth}px`
                        : undefined,
                    }
                  : {
                      width: triggerWidth > 0 ? `${triggerWidth}px` : "100%",
                      minWidth: dropdownMinWidth != null
                        ? `${Number(dropdownMinWidth)}px`
                        : (triggerWidth > 0 ? `${triggerWidth}px` : undefined),
                      maxWidth: dropdownMaxWidth != null
                        ? `${Number(dropdownMaxWidth)}px`
                        : (triggerWidth > 0 ? `${triggerWidth}px` : undefined),
                    }),
              ...(above
                ? { bottom: `${window.innerHeight - y + 3}px` }
                : { top: `${y + height + 3}px` }),
            }}
          >
            {sanitizedOptions.length === 0 && (
              <div className={"d-flex gap-2 align-items-center py-2 px-2 option"}>
                <p className="form-label flex-grow w-100 m-0 text-muted text-center" style={{ fontSize: "13px" }}>
                  {t("noData", { defaultValue: "No Data" })}
                </p>
              </div>
            )}
            {searchable && sanitizedOptions.length > 0 && (
              <div className="search-container mb-1">
                <input
                  ref={searchInputRef}
                  className="form-control"
                  type="text"
                  placeholder={t("commonUi.search", { defaultValue: "Search..." })}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </div>
            )}
            {searchable && sanitizedOptions.length > 0 && filteredOptions.length === 0 && (
              <div className="d-flex align-items-center justify-content-center py-3 text-muted" style={{ fontSize: "13px" }}>
                {t("commonUi.noData", { defaultValue: "No matches found" })}
              </div>
            )}

            {filteredOptions.map((opt, index) => (
              <div
                className={
                  "d-flex gap-2 align-items-center py-1 option" +
                  (String(opt.id) === String(selectedId) ||
                  (String(selectedId).toLowerCase() === "all" && String(opt.id) === "%") ||
                  (String(selectedId) === "%" && String(opt.id).toLowerCase() === "all")
                    ? " active"
                    : "")
                }
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOptionSelect(opt);
                }}
                key={`${opt.id}-${index}`}
              >
                <EllipsisTooltip
                  enabled={ellipsisTooltip}
                  title={getOptionLabelText(opt.label)}
                >
                  <p
                    className="form-label flex-grow w-100 m-0"
                    style={
                      ellipsisTooltip
                        ? {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                          }
                        : undefined
                    }
                  >
                    {opt.label}
                  </p>
                </EllipsisTooltip>
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default Select;
